/**
 * People named in a staff report, each marked Favorable, Neutral, or Unfavorable.
 * Staff or guests — kind defaults to staff so older reports still read.
 */

export const REPORT_CONTEXT_USER_MAX = 5;

export type ReportContextStanding = 'good' | 'neutral' | 'bad';

export type ReportContextPersonKind = 'staff' | 'guest';

export type ReportContextUser = {
  kind?: ReportContextPersonKind,
  staffId: string,
  customerId?: string,
  username: string,
  email: string,
  permission?: number,
  standing: ReportContextStanding,
};

export const reportContextPersonKind = (
  user: Pick<ReportContextUser, 'kind'>,
): ReportContextPersonKind => (user.kind === 'guest' ? 'guest' : 'staff');

export const isReportContextStanding = (value: unknown): value is ReportContextStanding => (
  value === 'good' || value === 'neutral' || value === 'bad'
);

/** Professional labels — stored keys stay good / neutral / bad for older reports. */
export const reportContextStandingLabel = (standing: ReportContextStanding): string => {
  if (standing === 'bad') return 'Unfavorable';
  if (standing === 'neutral') return 'Neutral';
  return 'Favorable';
};

/** /audits Actions types — low-level problem terming, one type per standing. */
export const REPORT_CONTEXT_STANDING_LOG_TYPES = [
  'Low-level favorable',
  'Low-level note',
  'Low-level problem',
] as const;

export type ReportContextStandingLogType = typeof REPORT_CONTEXT_STANDING_LOG_TYPES[number];

export const isReportContextStandingLogType = (
  type: unknown,
): type is ReportContextStandingLogType => (
  type === 'Low-level favorable' || type === 'Low-level note' || type === 'Low-level problem'
);

export const reportContextStandingLogType = (
  standing: ReportContextStanding,
): ReportContextStandingLogType => {
  if (standing === 'bad') return 'Low-level problem';
  if (standing === 'neutral') return 'Low-level note';
  return 'Low-level favorable';
};

const contextUserKey = (
  user: Pick<ReportContextUser, 'kind' | 'staffId' | 'customerId' | 'email'>,
): string => {
  const kind = reportContextPersonKind(user);
  if (kind === 'guest') {
    return `guest:${String(user.customerId || '').trim()
      || String(user.email || '').trim().toLowerCase()}`;
  }
  return `staff:${String(user.staffId || '').trim()
    || String(user.email || '').trim().toLowerCase()}`;
};

/** Drop blanks, dupes, and anything past the 5-person cap. */
export const sanitizeReportContextUsers = (raw: unknown): ReportContextUser[] => {
  if (!Array.isArray(raw)) return [];
  const seen = new Set<string>();
  const out: ReportContextUser[] = [];
  raw.forEach((item) => {
    if (out.length >= REPORT_CONTEXT_USER_MAX) return;
    if (!item || typeof item !== 'object') return;
    const row = item as Partial<ReportContextUser>;
    const standing = isReportContextStanding(row.standing) ? row.standing : 'neutral';
    const username = String(row.username || '').trim();
    const email = String(row.email || '').trim().toLowerCase();
    const staffId = String(row.staffId || '').trim();
    const customerId = String(row.customerId || '').trim();
    const kind = reportContextPersonKind(row);
    if (kind === 'guest') {
      if (!username && !email && !customerId) return;
    } else if (!username && !email && !staffId) {
      return;
    }
    const key = contextUserKey({ kind, staffId, customerId, email });
    if (!key || key === 'staff:' || key === 'guest:' || seen.has(key)) return;
    seen.add(key);
    const permission = Number(row.permission);
    if (kind === 'guest') {
      out.push({
        kind: 'guest',
        staffId: '',
        customerId,
        username: username || email || 'Guest',
        email,
        standing,
      });
      return;
    }
    out.push({
      kind: 'staff',
      staffId,
      username: username || email || 'Staff',
      email,
      standing,
      ...(Number.isFinite(permission) ? { permission } : {}),
    });
  });
  return out;
};

export const reportContextUserKey = contextUserKey;

/** Favorable / Unfavorable people require an alert-level summary on the report. */
export const reportNeedsAlertLevelSummary = (
  users: Pick<ReportContextUser, 'standing'>[],
): boolean => users.some((person) => person.standing === 'good' || person.standing === 'bad');

type ReportContextParty = {
  email?: string,
  staffId?: string | number | null,
};

const reportContextPartyIdentity = (party?: ReportContextParty) => ({
  email: String(party?.email || '').trim().toLowerCase(),
  staffId: party?.staffId != null && String(party.staffId).trim()
    ? String(party.staffId).trim()
    : '',
});

const staffMatchesContextParty = (
  person: ReportContextUser,
  party: { email: string, staffId: string },
): boolean => {
  if (reportContextPersonKind(person) !== 'staff') return false;
  if (party.staffId && person.staffId && person.staffId === party.staffId) return true;
  if (party.email && person.email && person.email === party.email) return true;
  return false;
};

/** Author and recipient already have a role — they cannot also be named in context. */
export const excludeReportPartiesFromContextUsers = (
  users: ReportContextUser[],
  parties: ReportContextParty[],
): ReportContextUser[] => {
  const identities = parties
    .map(reportContextPartyIdentity)
    .filter((party) => party.email || party.staffId);
  if (!identities.length) return users;
  return users.filter((person) => (
    !identities.some((party) => staffMatchesContextParty(person, party))
  ));
};
